import type { UVInsertConfig } from '@shared/models/uv-inserts';
import { cropImageToView, parseCssPixels, type ImageTransform } from '@uv-inserts/utils/cropImageToView';
import { logError } from '../utilities/logging.js';

/**
 * Crops each uploaded insert photo to the box it was framed in, so the printer gets the visible area
 * rather than the customer's whole original. Any photo that can't be cropped is sent as uploaded.
 */
export async function cropImagesForCart({
  config,
  imageByKey,
  imageTransformByKey
}: {
  config: UVInsertConfig;
  imageByKey: Record<string, File | null>;
  imageTransformByKey: Record<string, ImageTransform>;
}): Promise<Record<string, File | null>> {
  const result: Record<string, File | null> = { ...imageByKey };
  const elements = config.faceElements.flatMap((face) => face.elementConfig ?? []);

  await Promise.all(
    elements
      .filter((el) => el.type === 'IMAGE_UPLOAD')
      .map(async (el) => {
        const key = el.lineItemPropertyKey;
        const file = imageByKey[key];
        if (!(file instanceof File)) return;

        // Same 200px default the visualiser renders the box with (DevFaceVisualisers.tsx).
        const containerWidth = parseCssPixels(el.size?.width ?? '200px');
        const containerHeight = parseCssPixels(el.size?.height ?? '200px');
        if (containerWidth === null || containerHeight === null) {
          logError({
            label: 'cropImagesForCart',
            error: new Error('Insert photo box is not sized in pixels'),
            message: 'Sent the original upload.',
            extraData: { key, size: el.size }
          });
          return;
        }

        const cropped = await cropImageToView({ file, containerWidth, containerHeight, transform: imageTransformByKey[key] });
        if (cropped === file) {
          logError({
            label: 'cropImagesForCart',
            error: new Error('Insert photo crop failed'),
            message: 'Sent the original upload.',
            extraData: { key, name: file.name, type: file.type }
          });
          return;
        }
        result[key] = cropped;
      })
  );

  return result;
}
